import React from 'react';
import { Card } from 'react-bootstrap';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';

// Blood Clotting Function → Insights tab. Shows how "Viscosity - Capillary" was
// calculated for THIS test: the de Simone / Nwose high-shear formula with the
// patient's own hematocrit and total protein substituted in. Client-facing and
// non-diagnostic — it explains the number, it doesn't interpret it.

// Round to 2 decimals, then drop trailing zeros so entered values read cleanly
// (45 not 45.00, 7.2 not 7.20) while computed terms keep 2-decimal precision.
const trim = (n: number): string => String(Math.round(n * 100) / 100);
const fixed2 = (n: number): string => (Math.round(n * 100) / 100).toFixed(2);

const formulaStyle: React.CSSProperties = {
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: '14px',
  lineHeight: 1.9,
  color: '#20527f',
  whiteSpace: 'nowrap',
  overflowX: 'auto',
  display: 'block',
};

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
};

export class ViscosityInsights extends React.Component<Props, {}> {
  static shouldRender(bloodworkAnalysis: BloodworkAnalysis): boolean {
    const v = bloodworkAnalysis && bloodworkAnalysis.viscosityCapiliaries;
    return !!(v && v.hasValue());
  }

  // Shared formula computation for both the rendered card and the Notes digest.
  static compute(bloodworkAnalysis: BloodworkAnalysis): {
    hct: number;
    protein: number;
    inner: number;
    term1: number;
    term2: number;
    result: number;
  } | null {
    if (!ViscosityInsights.shouldRender(bloodworkAnalysis)) {
      return null;
    }
    const hct = bloodworkAnalysis.hct.value;
    const protein = bloodworkAnalysis.protein.value;
    const inner = protein * 10 - 2.07;
    return {
      hct,
      protein,
      inner,
      term1: 0.12 * hct,
      term2: 0.17 * inner,
      result: bloodworkAnalysis.viscosityCapiliaries.value,
    };
  }

  // Plain-text version of the insight for the Notes "gather from tabs" digest,
  // so Blood Clotting Function's Insights reads into Notes like other sections'.
  static digestGroup(
    bloodworkAnalysis: BloodworkAnalysis,
  ): { title: string; items: string[] } | null {
    const c = ViscosityInsights.compute(bloodworkAnalysis);
    if (!c) {
      return null;
    }
    // Mirror the rendered card's content (render, below) as plain-text lines so
    // the digest shows the SAME information the Insights tab shows — the full
    // step-by-step substitution, not a condensed summary.
    return {
      title: 'Viscosity - Capillary',
      items: [
        "Viscosity is not measured directly — it is estimated from this test's " +
          'hematocrit and total protein using the whole-blood viscosity formula ' +
          '(high shear / capillaries): 0.12 × HCT + 0.17 × (Total Protein × 10 − 2.07)',
        `Using this test's values (HCT ${trim(c.hct)}%, Total Protein ${trim(
          c.protein,
        )} g/dL):`,
        `= 0.12 × ${trim(c.hct)} + 0.17 × (${trim(c.protein)} × 10 − 2.07)`,
        `= 0.12 × ${trim(c.hct)} + 0.17 × ${fixed2(c.inner)}`,
        `= ${fixed2(c.term1)} + ${fixed2(c.term2)}`,
        `= ${fixed2(c.result)} cP`,
        'cP stands for centipoise, the standard unit of viscosity — a measure of ' +
          'how thick a fluid is and how strongly it resists flowing. A higher cP ' +
          'means thicker, more slowly-flowing blood; the value is best read ' +
          'against the reference range rather than on its own.',
      ],
    };
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    const c = ViscosityInsights.compute(bloodworkAnalysis);
    if (!c) {
      return null;
    }
    const { hct, protein, inner, term1, term2, result } = c;

    return (
      <Card style={{ marginBottom: 12 }}>
        <Card.Header style={{ fontWeight: 'bold' }}>
          Viscosity - Capillary
        </Card.Header>
        <Card.Body>
          <p style={{ marginBottom: 12 }}>
            Viscosity is not measured directly. It is estimated from this test's{' '}
            <strong>hematocrit</strong> and <strong>total protein</strong> using
            the whole-blood viscosity formula (high shear / capillaries):
          </p>
          <code style={formulaStyle}>
            0.12 × HCT + 0.17 × (Total Protein × 10 − 2.07)
          </code>
          <p style={{ margin: '10px 0 6px', color: '#5b6675' }}>
            Using this test's values (HCT {trim(hct)}%, Total Protein{' '}
            {trim(protein)} g/dL):
          </p>
          <code style={formulaStyle}>
            = 0.12 × {trim(hct)} + 0.17 × ({trim(protein)} × 10 − 2.07)
            <br />
            = 0.12 × {trim(hct)} + 0.17 × {fixed2(inner)}
            <br />
            = {fixed2(term1)} + {fixed2(term2)}
            <br />
            <strong>= {fixed2(result)} cP</strong>
          </code>
          <p style={{ margin: '14px 0 0', fontSize: '12px', color: '#5b6675' }}>
            <strong>cP</strong> stands for <strong>centipoise</strong>, the
            standard unit of viscosity — a measure of how thick a fluid is and
            how strongly it resists flowing. A higher cP means thicker,
            more slowly-flowing blood; the value is best read against the
            reference range rather than on its own.
          </p>
        </Card.Body>
      </Card>
    );
  }
}
